import React, { useState } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Typography,
  Box,
  Checkbox,
  Chip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useContactContext } from '../../contexts/ContactContext';

const ContactTable = ({ contacts, onToggleSelection, onSelectAll }) => {
  // These now refer only to the contacts on the current page
  const anySelected = contacts.some(contact => contact.selected);
  const allSelected = contacts.length > 0 && contacts.every(contact => contact.selected);
  
  // Get the delete function from context
  const { deleteContacts } = useContactContext();
  
  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactsToDelete, setContactsToDelete] = useState([]);

  // Delete confirmation handlers
  const handleOpenDeleteDialog = () => {
    // Get IDs of selected contacts
    const selectedIds = contacts.filter(contact => contact.selected).map(contact => contact.id);
    setContactsToDelete(selectedIds);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    const success = await deleteContacts(contactsToDelete);
    setDeleteDialogOpen(false);
    // Contact table will be updated through context after deletion
  };
    
  if (!contacts || contacts.length === 0) {
    return (
      <Box sx={{ 
        textAlign: 'center', 
        py: 5, 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center', 
        minHeight: '200px',
        bgcolor: '#f7f9fc',
        border: '1px dashed',
        borderColor: '#c2c9d6',
        borderRadius: 2
      }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No contacts found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try uploading contacts using the 'Upload Contacts' button or add contacts manually.
        </Typography>
      </Box>
    );
  }
  
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {anySelected && (
          <Tooltip title="Delete selected contacts">
            <Button 
              variant="contained" 
              color="error" 
              startIcon={<DeleteIcon />}
              onClick={handleOpenDeleteDialog}
            >
              Delete Selected ({contacts.filter(contact => contact.selected).length})
            </Button>
          </Tooltip>
        )}
      </Box>
      
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={anySelected && !allSelected}
                  onChange={(e) => onSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Job Title</TableCell>
              <TableCell>State</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>LinkedIn</TableCell>
              <TableCell>Created At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow
                key={contact.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={!!contact.selected}
                    onChange={() => onToggleSelection(contact.id)}
                  />
                </TableCell>
                <TableCell component="th" scope="row">
                  <Typography variant="body2">{contact.name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    component="a"
                    href={`mailto:${contact.email}`}
                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                  >
                    {contact.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{contact.phone}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{contact.jobTitle}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {contact.location.state}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {contact.location.city || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {contact.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" variant="outlined" />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {contact.linkedinUrl ? (
                      <a href={contact.linkedinUrl} target="_blank" rel="noopener noreferrer">
                        {contact.linkedinUrl}
                      </a>
                    ) : (
                      '-'
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {contact.createdAt ? contact.createdAt.toLocaleDateString() : '-'}
                  </Typography>
                </TableCell>

              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {"Delete Selected Contacts"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete {contactsToDelete.length} selected contact(s)? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContactTable;
